import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Mic } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EpisodesManager = () => {
  const [episodes, setEpisodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    audio_url: "",
    cover_image: "",
    duration: "",
    published_date: "",
    is_published: true,
    episode_number: ""
  });

  useEffect(() => {
    fetchEpisodes();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  });

  const fetchEpisodes = async () => {
    try {
      const response = await axios.get(`${API}/episodes`, getAuthHeader());
      setEpisodes(response.data);
    } catch (error) {
      toast.error("Failed to fetch episodes");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      audio_url: "",
      cover_image: "",
      duration: "",
      published_date: "",
      is_published: true,
      episode_number: ""
    });
    setEditingEpisode(null);
  };

  const handleOpenDialog = (episode = null) => {
    if (episode) {
      setEditingEpisode(episode);
      setFormData({
        title: episode.title || "",
        description: episode.description || "",
        audio_url: episode.audio_url || "",
        cover_image: episode.cover_image || "",
        duration: episode.duration || "",
        published_date: episode.published_date || "",
        is_published: episode.is_published ?? true,
        episode_number: episode.episode_number?.toString() || ""
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    const payload = {
      ...formData,
      episode_number: formData.episode_number ? parseInt(formData.episode_number) : null
    };

    try {
      if (editingEpisode) {
        await axios.put(`${API}/episodes/${editingEpisode.id}`, payload, getAuthHeader());
        toast.success("Episode updated successfully");
      } else {
        await axios.post(`${API}/episodes`, payload, getAuthHeader());
        toast.success("Episode created successfully");
      }
      handleCloseDialog();
      fetchEpisodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save episode");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this episode?")) return;

    try {
      await axios.delete(`${API}/episodes/${id}`, getAuthHeader());
      toast.success("Episode deleted");
      fetchEpisodes();
    } catch (error) {
      toast.error("Failed to delete episode");
    }
  };

  return (
    <div data-testid="episodes-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[#1A2323] mb-2">Episodes</h1>
          <p className="text-[#4A5555]">Manage your podcast episodes</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#2E8B8B] hover:bg-[#1F6666] text-white"
              onClick={() => handleOpenDialog()}
              data-testid="add-episode-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Episode
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {editingEpisode ? "Edit Episode" : "Add New Episode"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="episode_number">Episode Number</Label>
                  <Input
                    id="episode_number"
                    type="number"
                    placeholder="1"
                    value={formData.episode_number}
                    onChange={(e) => setFormData({ ...formData, episode_number: e.target.value })}
                    data-testid="episode-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration</Label>
                  <Input
                    id="duration"
                    placeholder="45:30"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    data-testid="episode-duration-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="Episode title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="episode-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Episode description..."
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="episode-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="audio_url">Audio URL</Label>
                <Input
                  id="audio_url"
                  placeholder="https://example.com/audio.mp3"
                  value={formData.audio_url}
                  onChange={(e) => setFormData({ ...formData, audio_url: e.target.value })}
                  data-testid="episode-audio-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cover_image">Cover Image URL</Label>
                <Input
                  id="cover_image"
                  placeholder="https://example.com/cover.jpg"
                  value={formData.cover_image}
                  onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                  data-testid="episode-cover-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="published_date">Published Date</Label>
                <Input
                  id="published_date"
                  type="date"
                  value={formData.published_date}
                  onChange={(e) => setFormData({ ...formData, published_date: e.target.value })}
                  data-testid="episode-date-input"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_published"
                  checked={formData.is_published}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                  data-testid="episode-published-switch"
                />
                <Label htmlFor="is_published">Published</Label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCloseDialog}
                  data-testid="cancel-episode-btn"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#2E8B8B] hover:bg-[#1F6666] text-white"
                  disabled={isSaving}
                  data-testid="save-episode-btn"
                >
                  {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingEpisode ? "Update" : "Create"} Episode
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-[#E5E0D6]">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#2E8B8B]" />
            </div>
          ) : episodes.length === 0 ? (
            <div className="text-center py-12">
              <Mic className="w-12 h-12 text-[#E67E22] mx-auto mb-4 opacity-50" />
              <p className="text-[#4A5555]">No episodes yet. Add your first episode!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F2EB]">
                  <TableHead className="font-semibold text-[#1A2323]">#</TableHead>
                  <TableHead className="font-semibold text-[#1A2323]">Title</TableHead>
                  <TableHead className="font-semibold text-[#1A2323]">Duration</TableHead>
                  <TableHead className="font-semibold text-[#1A2323]">Status</TableHead>
                  <TableHead className="font-semibold text-[#1A2323] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {episodes.map((episode) => (
                  <TableRow key={episode.id} data-testid={`episode-row-${episode.id}`}>
                    <TableCell className="font-medium">{episode.episode_number || "-"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {episode.cover_image && (
                          <img 
                            src={episode.cover_image} 
                            alt={episode.title}
                            className="w-10 h-10 rounded object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium text-[#1A2323]">{episode.title}</p>
                          <p className="text-sm text-[#4A5555] line-clamp-1">{episode.description}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{episode.duration || "-"}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        episode.is_published 
                          ? "bg-[#2E8B8B]/10 text-[#2E8B8B]" 
                          : "bg-[#4A5555]/10 text-[#4A5555]"
                      }`}>
                        {episode.is_published ? "Published" : "Draft"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleOpenDialog(episode)}
                          data-testid={`edit-episode-${episode.id}`}
                        >
                          <Pencil className="w-4 h-4 text-[#4A5555]" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(episode.id)}
                          data-testid={`delete-episode-${episode.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EpisodesManager;
